import {
  GEAR_SLOTS,
  GEAR_SLOT_NAME,
  INVENTORY,
  ITEMS,
  ITEM_IDS,
  RECIPES,
  TILES,
  count,
  craftProblem,
  iconTile,
  isItemId,
  placesAs,
  slotOf,
  slotsUsed,
  usedIn,
  type EditTarget,
  type Gear,
  type GearSlot,
  type Inventory,
  type ItemId,
} from "@dyson/shared";
import { drawItemIcon } from "../render/TileAtlas";
import { slotFromDrag } from "./Hud";

/**
 * Inventory and crafting, opened with I. Shows what you wear (six slots:
 * head, body, legs, hands, feet and back; click a wearable item to put it on,
 * a worn one to take it off), lists what you carry that isn't on the
 * quick-access bar (drag between the two), and every
 * recipe; recipes marked "workbench" only work standing by one. Opening a
 * chest (right click it) opens this too, with the chest's contents on top.
 * Below it all is the guide: every item and what it's for.
 */
export class CraftingPanel {
  onCraft: (recipeId: string) => void = () => {};
  /** A quick-access slot was dragged back into the inventory: take it off the bar. */
  onReturn: (slot: number) => void = () => {};
  /** An inventory item was clicked (or tapped): put it in the selected quick-access slot. */
  onPick: (item: ItemId) => void = () => {};
  /** Move `n` of `item` into the open chest (n > 0) or out of it (n < 0). */
  onChestMove: (item: ItemId, n: number) => void = () => {};
  /** Put on a wearable item you carry (clicked, or dropped on its slot). */
  onEquip: (item: ItemId) => void = () => {};
  /** Take off what's worn in a slot (clicked). */
  onUnequip: (slot: GearSlot) => void = () => {};

  private readonly root = document.getElementById("craft")!;
  private readonly items = this.root.querySelector<HTMLElement>(".craft-items:not(.chest-items)")!;
  private readonly gearSlots = this.root.querySelector<HTMLElement>(".gear-slots")!;
  private readonly chestBox = this.root.querySelector<HTMLElement>(".chest")!;
  private readonly chestItems = this.root.querySelector<HTMLElement>(".chest-items")!;
  private readonly recipes = this.root.querySelector<HTMLElement>(".craft-recipes")!;
  private readonly bench = this.root.querySelector<HTMLElement>(".craft-bench")!;
  private readonly packLabel = this.root.querySelector<HTMLElement>(".craft-pack")!;
  private readonly guide = this.root.querySelector<HTMLElement>(".guide")!;
  private readonly guideToggle = this.root.querySelector<HTMLButtonElement>(".guide-toggle")!;
  private readonly guideFilter = this.root.querySelector<HTMLInputElement>(".guide-filter")!;
  private readonly guideList = this.root.querySelector<HTMLElement>(".guide-list")!;
  /** Guide rows with the lower-case text the filter matches against; built the first time it's opened. */
  private guideRows: { row: HTMLElement; text: string }[] = [];
  /** What the panel last drew, so it only redraws when something changed. */
  private drawn = "";
  /** What we carry, as last drawn. */
  private inv: Inventory = {};
  /** The item under the mouse, so pressing 1-9 can put it on the quick-access bar. */
  hovered: ItemId | null = null;
  /** The open chest (its anchor tile) and what the server last said is in it. */
  chest: { target: EditTarget; items: Inventory } | null = null;

  constructor() {
    this.root.querySelector(".craft-close")!.addEventListener("click", () => this.close());
    this.guideToggle.addEventListener("click", () => {
      const open = this.guide.classList.toggle("hidden") === false;
      this.guideToggle.textContent = open ? "Guide ▾" : "Guide ▸";
      if (open && !this.guideRows.length) this.buildGuide();
    });
    this.guideFilter.addEventListener("input", () => this.filterGuide());
    // Escape leaves the filter box (the game ignores keys while it has focus).
    this.guideFilter.addEventListener("keydown", (e) => {
      if (e.key === "Escape" || e.key === "Enter") this.guideFilter.blur();
    });
    this.root.addEventListener("dragover", (e) => e.preventDefault());
    this.root.addEventListener("drop", (e) => {
      e.preventDefault();
      const data = e.dataTransfer?.getData("text/plain") ?? "";
      const slot = slotFromDrag(data);
      const intoChest = !!(e.target as HTMLElement).closest(".chest");
      const ontoGear = !!(e.target as HTMLElement).closest(".gear-slots");
      if (slot !== null) this.onReturn(slot);
      // An item dropped on what you wear: put it on (the server checks it's wearable, and that you have it).
      else if (ontoGear && isItemId(data)) this.onEquip(data);
      // Whole stacks dragged between the chest and the inventory.
      else if (!this.chest) return;
      else if (intoChest && isItemId(data)) this.onChestMove(data, count(this.inv, data));
      else if (!intoChest && data.startsWith("chest:")) {
        const id = data.slice(6);
        if (isItemId(id)) this.onChestMove(id, -count(this.chest.items, id));
      }
    });
  }

  get isOpen(): boolean {
    return !this.root.classList.contains("hidden");
  }

  toggle(): void {
    if (this.isOpen) this.close();
    else this.root.classList.remove("hidden");
    this.drawn = "";
  }

  close(): void {
    this.hovered = null;
    this.chest = null;
    this.root.classList.add("hidden");
  }

  /** Show what's in a chest (opening the panel if it isn't). */
  showChest(target: EditTarget, items: Inventory): void {
    this.chest = { target, items };
    this.root.classList.remove("hidden");
  }

  /** Redraw if anything changed. `onBar` is what's on the quick-access bar: those items live there, not here. `gear`: what you wear. */
  update(inv: Inventory, atBench: boolean, onBar: Set<ItemId>, gear: Gear): void {
    if (!this.isOpen) return;
    const key = JSON.stringify(inv) + atBench + [...onBar].join() + JSON.stringify(this.chest) + JSON.stringify(gear);
    if (key === this.drawn) return;
    this.drawn = key;
    this.inv = inv;

    this.gearSlots.replaceChildren(...GEAR_SLOTS.map((slot) => this.gearCell(slot, gear[slot])));

    this.chestBox.classList.toggle("hidden", !this.chest);
    this.chestItems.replaceChildren();
    if (this.chest) {
      const inside = ITEM_IDS.filter((id) => count(this.chest!.items, id) > 0);
      if (!inside.length) this.chestItems.textContent = "Empty. Click your items below to put them in.";
      for (const id of inside) this.chestItems.append(this.chestCell(id, count(this.chest.items, id)));
    }

    this.bench.textContent = atBench ? "At a workbench: everything can be made here." : "Stand by a workbench for the recipes marked workbench.";
    this.bench.classList.toggle("ok", atBench);

    const used = slotsUsed(inv);
    this.packLabel.textContent = `Pack: ${used}/${INVENTORY.slots} stacks`;
    this.packLabel.classList.toggle("warn", used >= INVENTORY.slots);

    this.items.replaceChildren();
    this.hovered = null;
    // With a chest open, everything you carry can go in it, on the bar or not.
    const held = ITEM_IDS.filter((id) => count(inv, id) > 0 && (this.chest || !onBar.has(id)));
    if (!held.length) this.items.textContent = onBar.size ? "Everything you carry is on the quick-access bar." : "Nothing yet. Dig rock, ore, coal and plants to collect them.";
    for (const id of held) this.items.append(this.itemCell(id, count(inv, id)));

    this.recipes.replaceChildren();
    for (const r of RECIPES) {
      const problem = craftProblem(inv, r, atBench);
      const row = document.createElement("div");
      row.className = "recipe" + (problem ? " locked" : "");
      const needs = (Object.keys(r.needs) as ItemId[])
        .map((id) => `${r.needs[id]} ${ITEMS[id].name}` + (count(inv, id) < r.needs[id]! ? ` (have ${count(inv, id)})` : ""))
        .join(", ");
      const text = document.createElement("div");
      text.className = "recipe-text";
      text.innerHTML = `<b>${r.count > 1 ? `${r.count}× ` : ""}${ITEMS[r.makes].name}</b><span class="small">${needs}</span>`;
      if (r.workbench) {
        const tag = document.createElement("span");
        tag.className = "tag";
        tag.textContent = "workbench";
        text.querySelector("b")!.append(" ", tag);
      }
      const button = document.createElement("button");
      button.textContent = "Craft";
      button.disabled = !!problem;
      button.title = problem ?? "";
      button.addEventListener("click", () => this.onCraft(r.id));
      row.append(icon(r.makes), text, button);
      this.recipes.append(row);
    }
  }

  /**
   * The recipe book: every item, what it's for, what it goes into and how
   * it's dug. It never changes, so it's drawn once.
   */
  private buildGuide(): void {
    const names = (ids: ItemId[]) => [...new Set(ids)].map((id) => ITEMS[id].name).join(", ");
    for (const id of ITEM_IDS) {
      const def = ITEMS[id];
      const row = document.createElement("div");
      row.className = "guide-row";
      const text = document.createElement("div");
      text.className = "recipe-text";
      const lines: string[] = [];
      if (def.desc) lines.push(def.desc);
      if (def.heals) lines.push(`Eat it to heal ${def.heals}.`);
      const makes = RECIPES.filter((r) => r.makes === id);
      if (makes.length) lines.push(`Made from: ${makes.map((r) => names(Object.keys(r.needs) as ItemId[])).join("; or ")}${makes.some((r) => r.workbench) ? " (workbench)" : ""}`);
      const uses = usedIn(id);
      if (uses.length) lines.push(`Used in: ${names(uses.map((r) => r.makes))}`);
      const dig = digText(id);
      if (dig) lines.push(dig);
      const name = document.createElement("b");
      name.textContent = def.name;
      text.append(name);
      for (const line of lines) {
        const span = document.createElement("span");
        span.className = "small";
        span.textContent = line;
        text.append(span);
      }
      row.append(icon(id), text);
      this.guideList.append(row);
      this.guideRows.push({ row, text: `${def.name} ${lines.join(" ")}`.toLowerCase() });
    }
    this.filterGuide();
  }

  private filterGuide(): void {
    const q = this.guideFilter.value.trim().toLowerCase();
    for (const { row, text } of this.guideRows) row.classList.toggle("hidden", !!q && !text.includes(q));
  }

  /**
   * An inventory item: drag it onto the quick-access bar, or hover it and
   * press 1-9. With a chest open, click to put the stack in (right click: one).
   * Something wearable is put on with a click.
   */
  private itemCell(id: ItemId, n: number): HTMLElement {
    const cell = document.createElement("div");
    cell.className = "item";
    const wearable = !!slotOf(id);
    cell.title = this.chest
      ? `${ITEMS[id].name}: click to put in the chest (right click: one)`
      : wearable
        ? `${ITEMS[id].name}: click to put it on (${GEAR_SLOT_NAME[slotOf(id)!].toLowerCase()}), or drag it onto the bar`
        : `${ITEMS[id].name}: click to put it in the selected slot, drag it onto the bar, or hover and press 1-9`;
    cell.draggable = true;
    cell.addEventListener("dragstart", (e) => e.dataTransfer?.setData("text/plain", id));
    cell.addEventListener("mouseenter", () => (this.hovered = id));
    cell.addEventListener("mouseleave", () => (this.hovered = this.hovered === id ? null : this.hovered));
    if (this.chest) moveOnClick(cell, (all) => this.onChestMove(id, all ? n : 1));
    else if (wearable) cell.addEventListener("click", () => this.onEquip(id));
    // With no chest, a click (or a tap: there's no dragging on a touch screen) puts it in the selected slot.
    else cell.addEventListener("click", () => this.onPick(id));
    cell.append(icon(id), countLabel(n));
    return cell;
  }

  /** One of the slots you wear things in: what's there (click to take it off), or its name when it's empty. */
  private gearCell(slot: GearSlot, worn: ItemId | undefined): HTMLElement {
    const cell = document.createElement("div");
    cell.className = "item gear-slot" + (worn ? " worn" : "");
    cell.dataset.slot = slot;
    cell.title = worn ? `${ITEMS[worn].name} (${GEAR_SLOT_NAME[slot].toLowerCase()}): click to take it off` : `${GEAR_SLOT_NAME[slot]}: nothing worn. Drop something wearable here, or click it below.`;
    if (worn) {
      cell.append(icon(worn));
      cell.addEventListener("click", () => this.onUnequip(slot));
    } else {
      const empty = document.createElement("span");
      empty.className = "empty";
      cell.append(empty);
    }
    const label = document.createElement("span");
    label.className = "label";
    label.textContent = GEAR_SLOT_NAME[slot];
    cell.append(label);
    return cell;
  }

  /** An item in the open chest: click (or drag it out) to take the stack, right click to take one. */
  private chestCell(id: ItemId, n: number): HTMLElement {
    const cell = document.createElement("div");
    cell.className = "item";
    cell.title = `${ITEMS[id].name}: click to take (right click: one)`;
    cell.draggable = true;
    cell.addEventListener("dragstart", (e) => e.dataTransfer?.setData("text/plain", `chest:${id}`));
    moveOnClick(cell, (all) => this.onChestMove(id, all ? -n : -1));
    cell.append(icon(id), countLabel(n));
    return cell;
  }
}

/** Click moves the whole stack, right click just one. */
function moveOnClick(cell: HTMLElement, move: (all: boolean) => void): void {
  cell.addEventListener("click", () => move(true));
  cell.addEventListener("contextmenu", (e) => {
    e.preventDefault();
    move(false);
  });
}

/**
 * How an item is dug, for the guide: from the block it places as, or else the
 * natural block it's dug out of ("Found as Ore: dig with a stone pickaxe").
 */
function digText(id: ItemId): string | null {
  const own = placesAs(id);
  const tile = iconTile(id);
  const def = tile === undefined ? undefined : TILES[tile];
  if (!def) return null;
  const tier = def.minTier && def.minTier !== "wood" ? `${def.minTier} ` : "";
  const how = !def.tool ? "dig with anything, even bare hands" : def.needsTool ? `dig with a ${tier}${def.tool}` : `dig fastest with a ${tier}${def.tool} (bare hands work, slowly)`;
  return own === undefined ? `Found as ${def.name}: ${how}.` : `Placed as a block: ${how}.`;
}

function countLabel(n: number): HTMLElement {
  const label = document.createElement("span");
  label.className = "count";
  label.textContent = String(n);
  return label;
}

function icon(id: ItemId): HTMLCanvasElement {
  const c = document.createElement("canvas");
  c.width = c.height = 32;
  drawItemIcon(id, c);
  return c;
}
